import { QueryClientProvider } from "@tanstack/react-query";
import {
  ActivityIcon,
  ChartColumnIcon,
  LayoutDashboardIcon,
  PresentationIcon,
  ShieldAlertIcon,
  StoreIcon,
  TrendingUpIcon,
  UsersIcon,
  WarehouseIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { TooltipProvider } from "@/components/ui/tooltip";
import { matchPath, navigate, useLocation } from "@/lib/pages";
import { authClient } from "./auth";
import { CustomersPage } from "./pages/customers";
import { DemoPage } from "./pages/demo";
import { GaragesPage } from "./pages/garages";
import { GrowthPage } from "./pages/growth";
import { HealthPage } from "./pages/health";
import { MisusePage } from "./pages/misuse";
import { OverviewPage } from "./pages/overview";
import { PasskeysPage } from "./pages/passkeys";
import { RegisterPage, SignInPage } from "./pages/sign-in";
import { SourcingPage } from "./pages/sourcing";
import { UsagePage } from "./pages/usage";
import { createAdminQueryClient } from "./query";
import { type AdminPage, Shell } from "./shell";
import { coreClient, TRPCProvider } from "./trpc";

/** The admin app's pages, in the navigation's order: one per question of the owner (ADR 0016). */
export const PAGES: AdminPage[] = [
  {
    path: "/",
    key: "overview",
    icon: LayoutDashboardIcon,
    render: () => <OverviewPage />,
    installation: true,
  },
  {
    path: "/health",
    key: "health",
    icon: ActivityIcon,
    render: () => <HealthPage />,
    installation: true,
    range: true,
  },
  ...(
    [
      ["/growth", "growth", TrendingUpIcon, () => <GrowthPage />],
      ["/customers", "customers", UsersIcon, () => <CustomersPage />],
      ["/usage", "usage", ChartColumnIcon, () => <UsagePage />],
      ["/sourcing", "sourcing", StoreIcon, () => <SourcingPage />],
      ["/garages", "garages", WarehouseIcon, () => <GaragesPage />],
      ["/misuse", "misuse", ShieldAlertIcon, () => <MisusePage />],
      ["/demo", "demo", PresentationIcon, () => <DemoPage />],
    ] as const
  ).map(([path, key, icon, render]) => ({
    path,
    key,
    icon,
    render,
    installation: true,
    range: true,
  })),
];

export function AdminAppRoot() {
  const [queryClient] = useState(createAdminQueryClient);
  return (
    <QueryClientProvider client={queryClient}>
      <TRPCProvider trpcClient={coreClient} queryClient={queryClient}>
        <TooltipProvider delayDuration={300}>
          <Routes />
        </TooltipProvider>
      </TRPCProvider>
    </QueryClientProvider>
  );
}

function Routes() {
  const { pathname } = useLocation();
  const register = matchPath("/register/:token", pathname);
  if (register) return <RegisterPage token={register.token ?? ""} />;
  if (pathname === "/sign-in") return <SignInPage />;
  return <SignedIn pathname={pathname} />;
}

/** Every other page needs a session; without one it is the sign-in page. */
function SignedIn({ pathname }: { pathname: string }) {
  const { t } = useTranslation();
  const session = authClient.useSession();
  const signedOut = !session.isPending && !session.data;
  useEffect(() => {
    if (signedOut) navigate("/sign-in", { replace: true });
  }, [signedOut]);
  if (!session.data) return null;

  const email = session.data.user.email;
  if (pathname === "/passkeys") {
    return (
      <Shell
        pages={PAGES}
        current={null}
        email={email}
        extra={{ title: t("adminApp.nav.passkeys"), content: <PasskeysPage /> }}
      />
    );
  }
  const current = PAGES.find((page) => page.path === pathname) ?? PAGES[0] ?? null;
  return <Shell pages={PAGES} current={current} email={email} />;
}
